/**
 * MenuReorderTab - Drag and drop reordering of menu items within categories
 */
import { useState, useEffect } from 'react';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors 
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Save, RotateCcw, Coffee, Check, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { api } from '@/utils/apiClient';

// Sortable menu item component
function SortableMenuItem({ item, index }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 'auto',
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
        isDragging 
          ? 'bg-primary/10 border-primary shadow-lg scale-[1.02]' 
          : 'bg-card border-border hover:border-primary/50'
      }`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted"
      >
        <GripVertical className="w-5 h-5 text-muted-foreground" />
      </div>

      {/* Position Number */}
      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
        {index + 1}
      </div>

      {/* Item Image */}
      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
        <img 
          src={item.image} 
          alt={item.name} 
          className="w-full h-full object-cover"
        />
      </div>

      {/* Item Details */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-foreground truncate">{item.name}</h4>
        <p className="text-sm text-muted-foreground">${item.price.toFixed(2)}</p>
      </div>

      {/* Availability Badge */}
      {!item.is_available && (
        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex-shrink-0">
          Unavailable
        </span>
      )}
    </div>
  );
}

export default function MenuReorderTab({ items, categories, categoryLabels, onRefresh }) {
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id || 'coffee');
  const [categoryItems, setCategoryItems] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [originalOrder, setOriginalOrder] = useState([]);

  // Setup DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter and sort items when category changes
  useEffect(() => {
    const filtered = items
      .filter(item => item.category === activeCategory)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    setCategoryItems(filtered);
    setOriginalOrder(filtered.map(i => i.id));
    setHasChanges(false);
  }, [activeCategory, items]);

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setCategoryItems((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Check if order changed from original
        const newOrderIds = newOrder.map(i => i.id);
        setHasChanges(JSON.stringify(newOrderIds) !== JSON.stringify(originalOrder));
        
        return newOrder;
      });
    }
  };

  // Save the new order
  const saveOrder = async () => {
    setSaving(true);
    try {
      const orderData = categoryItems.map((item, index) => ({
        id: item.id,
        display_order: index
      }));

      await api.put('/menu/reorder', { items: orderData, category: activeCategory });
      toast.success('Menu order saved! Changes will appear on all screens.');
      setOriginalOrder(categoryItems.map(i => i.id));
      setHasChanges(false);
      onRefresh();
    } catch (err) {
      console.error('Failed to save order:', err);
      toast.error(err.response?.data?.detail || 'Failed to save order');
    }
    setSaving(false);
  };

  // Reset to original order
  const resetOrder = () => {
    const filtered = items
      .filter(item => item.category === activeCategory)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    setCategoryItems(filtered);
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl text-foreground flex items-center gap-2">
            <ArrowUpDown className="w-6 h-6" />
            Reorder Menu Items
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Drag and drop items to change their display order. Changes apply to menu, kiosk, and all screens.
          </p>
        </div>
        
        {hasChanges && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex gap-2"
          >
            <Button
              variant="outline"
              onClick={resetOrder}
              className="rounded-full"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={saveOrder}
              disabled={saving}
              className="rounded-full bg-primary hover:bg-primary/90"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Order'}
            </Button>
          </motion.div>
        )}
      </div>

      {/* Category Selector */}
      <div className="flex flex-wrap gap-2">
        {categories.filter(c => c.is_active !== false).map(cat => {
          const count = items.filter(i => i.category === cat.id).length;
          const isActive = activeCategory === cat.id;
          
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isActive
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-card hover:bg-muted text-foreground border border-border'
              }`}
              data-testid={`reorder-category-${cat.id}`}
            >
              {cat.name} ({count})
            </button>
          );
        })}
      </div>

      {/* Sortable Items List */}
      {categoryItems.length > 0 ? (
        <div className="bg-muted/30 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <GripVertical className="w-4 h-4" />
            <span>Drag items to reorder. Position 1 appears first on all screens.</span>
          </div>
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={categoryItems.map(i => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {categoryItems.map((item, index) => (
                  <SortableMenuItem
                    key={item.id}
                    item={item}
                    index={index}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground bg-muted/30 rounded-2xl">
          <Coffee className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No items in this category yet.</p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 text-sm">
        <p className="font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
          <Check className="w-4 h-4" />
          Where this order applies:
        </p>
        <ul className="text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
          <li>Public Menu page (mobile & desktop)</li>
          <li>Kiosk ordering screens</li>
          <li>Server/Staff ordering interface</li>
          <li>Kitchen display system</li>
          <li>Any other menu display throughout the site</li>
        </ul>
      </div>
    </div>
  );
}
